import { useState } from "react"
import { Button } from "../components/ui/button"
import { Badge } from "../components/ui/badge"
import {
    Dialog,
    DialogContent,
} from "../components/ui/dialog"
import { Input } from "../components/ui/input"
import { Textarea } from "../components/ui/textarea"
import { Target, Plus, Globe, ArrowRight, Loader2, MoreVertical, Trash2, MessageSquare } from "lucide-react";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
} from "../components/ui/dropdown-menu"

export function CMOOverview({
    cmoPlans,
    isLoading,
    onCreateNewPlan,
    onToggleChat,
    isChatOpen,
    onSelectPlan,
    showCreateForm,
    setShowCreateForm,
    websiteUrl,
    setWebsiteUrl,
    manualDescription,
    setManualDescription,
    showManualInput,
    setShowManualInput,
    onAnalyzeBrand,
    onDeletePlan
}) {
    const [urlError, setUrlError] = useState("")

    const isValidUrl = (value) => {
        try {
            const url = new URL(value)
            return (url.protocol === "http:" || url.protocol === "https:") && url.hostname.includes(".")
        } catch {
            return false
        }
    }
    const getStatusColor = (status) => {
        switch (status) {
            case "active":
                return "bg-green-50 text-green-600 border-green-200 dark:bg-green-500/10 dark:text-green-400 dark:border-green-500/20"
            case "completed":
                return "bg-blue-50 text-blue-600 border-blue-200 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20"
            case "draft":
                return "bg-gray-100 text-gray-600 border-gray-200 dark:bg-gray-500/10 dark:text-gray-400 dark:border-gray-500/20"
            default:
                return undefined;
        }
    }

    return (
        <div className="flex-1 min-w-0 overflow-y-auto py-6 px-4 sm:px-6 lg:px-10">
            <div className="max-w-4xl mx-auto min-w-0">
                <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                    <div className="min-w-0">
                        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
                            AI Marketing
                        </h1>
                        <p className="text-sm mt-1 text-gray-500 dark:text-gray-400">
                            Manage your marketing strategies and ad campaigns
                        </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <Button
                            type="button"
                            variant="outline"
                            onClick={onToggleChat}
                            aria-expanded={isChatOpen}
                            className="h-10 px-4 rounded-xl border-gray-200 text-gray-700 bg-white hover:bg-gray-50 dark:border-white/10 dark:bg-white/5 dark:text-gray-200 dark:hover:bg-white/10"
                        >
                            <MessageSquare className="w-4 h-4 mr-2" />
                            Ask AI CMO
                        </Button>
                        <Button
                            onClick={onCreateNewPlan}
                            className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-10 px-4 shrink-0"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            New Project
                        </Button>
                    </div>
                </div>

                {isLoading ? (
                    <div className="flex flex-col items-center justify-center p-20">
                        <Loader2 className="w-8 h-8 animate-spin text-orange-500 mb-4" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">Loading your marketing plans...</p>
                    </div>
                ) : cmoPlans.length > 0 ? (
                    <div className="mb-8">
                        <h2 className="text-xs font-medium uppercase tracking-wider mb-3 text-gray-500 dark:text-gray-400">
                            Your Projects
                        </h2>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {cmoPlans.map((plan) => (
                                <div
                                    key={plan.id}
                                    className="relative group min-w-0 text-left p-4 rounded-xl border transition-all hover:shadow-md bg-white border-gray-200 hover:border-gray-300 dark:bg-white/5 dark:border-white/10 dark:hover:bg-white/10 dark:hover:border-white/20 cursor-pointer"
                                    onClick={() => onSelectPlan(plan.id)}
                                >
                                    <div className="absolute top-3 right-2 z-10" onClick={(e) => e.stopPropagation()}>
                                        <DropdownMenu>
                                            <DropdownMenuTrigger className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors">
                                                <MoreVertical className="w-4 h-4" />
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="right" className="w-32">
                                                <DropdownMenuItem
                                                    onClick={() => onDeletePlan(plan.id)}
                                                    className="text-red-500 hover:text-red-600 dark:text-red-400 dark:hover:text-red-300"
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Delete
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </div>

                                    <div className="flex items-start gap-3 mb-3 pr-6">
                                        <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-gradient-to-br from-orange-100 via-white to-sky-100 border border-slate-200 shadow-inner dark:bg-slate-900 dark:bg-none dark:border-slate-800">
                                            {plan.logo ? (
                                                <img src={plan.logo} alt={plan.name} className="w-full h-full object-contain drop-shadow-[0_1px_1px_rgba(15,23,42,0.35)]" />
                                            ) : (
                                                <Target className="w-5 h-5 text-orange-500" />
                                            )}
                                        </div>
                                        <div className="min-w-0 flex-1">
                                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-0.5">
                                                <h3 className="min-w-0 font-medium text-sm truncate text-gray-900 dark:text-white">
                                                    {plan.name}
                                                </h3>
                                                <Badge className={`text-[10px] px-1.5 py-0.5 border flex-shrink-0 capitalize ${getStatusColor(plan.status)}`}>
                                                    {plan.status}
                                                </Badge>
                                            </div>
                                            <p className="text-xs truncate text-gray-400 dark:text-gray-500">
                                                {plan.primaryMarketingGoal ? `${plan.primaryMarketingGoal}${plan.productStage ? ` • ${plan.productStage}` : ''}` : plan.website || plan.description}
                                            </p>
                                            {plan.monthlyBudget !== undefined && plan.monthlyBudget > 0 && (
                                                <p className="text-[10px] font-semibold mt-1 uppercase text-orange-500 dark:text-orange-400">
                                                    Budget: ${plan.monthlyBudget.toLocaleString()}/mo
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-400 dark:text-gray-500">
                                        <span>{plan.conceptsCount} concepts</span>
                                        <span>{plan.adsGenerated} ads</span>
                                        <span className="sm:ml-auto">{plan.lastUpdated}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="rounded-2xl border p-12 text-center bg-white border-gray-200 dark:bg-white/5 dark:border-white/10">
                        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 bg-orange-50 dark:bg-orange-500/10">
                            <Target className="w-8 h-8 text-orange-500" />
                        </div>
                        <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                            No CMO projects yet
                        </h3>
                        <p className="text-sm mb-6 max-w-sm mx-auto text-gray-600 dark:text-gray-400">
                            Create your first AI CMO project to get a complete marketing strategy, ad concepts, and content for your brand.
                        </p>
                        <Button
                            onClick={onCreateNewPlan}
                            className="bg-orange-500 hover:bg-orange-600 text-white rounded-xl h-11 px-6"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            New Project
                        </Button>
                    </div>
                )}

                <Dialog open={showCreateForm} onOpenChange={setShowCreateForm}>
                    <DialogContent
                        onClose={() => setShowCreateForm(false)}
                        className="max-w-md overflow-hidden"
                    >
                        <div className="text-center mb-6">
                            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl mb-3 bg-orange-50 dark:bg-orange-500/10">
                                <Target className="w-7 h-7 text-orange-500" />
                            </div>
                            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                                Get your marketing plan
                            </h3>
                            <p className="text-xs italic mt-1 mb-2 text-gray-600 dark:text-gray-400">
                                {"We'll map the strategy first, then turn it into creative."}
                            </p>
                            <p className="text-sm text-gray-700 dark:text-gray-400">
                                Your AI CMO creates your strategy, ads, and content based on your brand, audience, and what's working.
                            </p>
                        </div>

                        {!showManualInput ? (
                            <div className="space-y-4">
                                {urlError && (
                                    <p className="flex items-center gap-1.5 text-xs text-red-500 font-medium -mb-2">
                                        <svg className="w-3.5 h-3.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                        {urlError}
                                    </p>
                                )}
                                <div className="relative">
                                    <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 dark:text-gray-500" />
                                    <Input
                                        type="url"
                                        placeholder="Paste your website — we'll do the rest"
                                        value={websiteUrl}
                                        onChange={(e) => {
                                            const val = e.target.value
                                            setWebsiteUrl(val)
                                            if (!val) {
                                                setUrlError("")
                                            } else if (!isValidUrl(val)) {
                                                setUrlError("Please enter a valid URL (e.g. https://yourwebsite.com)")
                                            } else {
                                                setUrlError("")
                                            }
                                        }}
                                        className={`h-12 pl-12 pr-4 text-base rounded-xl bg-white text-black placeholder:text-gray-400 dark:bg-white/5 dark:text-white dark:placeholder:text-gray-500 ${urlError
                                            ? "border-red-400 dark:border-red-500 focus-visible:border-red-400 focus-visible:ring-red-400/50"
                                            : "border-gray-200 dark:border-white/10"
                                            }`}
                                    />
                                </div>

                                <Button
                                    onClick={onAnalyzeBrand}
                                    disabled={!websiteUrl || !!urlError}
                                    className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-base font-medium"
                                >
                                    Get my plan
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>

                                <button
                                    onClick={() => setShowManualInput(true)}
                                    className="w-full text-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                                >
                                    No website? Describe your product instead
                                </button>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <Textarea
                                    placeholder="Describe your product or service..."
                                    value={manualDescription}
                                    onChange={(e) => setManualDescription(e.target.value)}
                                    rows={4}
                                    className="rounded-xl resize-none bg-white border-gray-200 text-black placeholder:text-gray-400 dark:bg-white/5 dark:border-white/10 dark:text-white dark:placeholder:text-gray-500"
                                />

                                <Button
                                    onClick={onAnalyzeBrand}
                                    disabled={!manualDescription}
                                    className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-white rounded-xl text-base font-medium"
                                >
                                    Analyze Brand
                                    <ArrowRight className="ml-2 w-5 h-5" />
                                </Button>

                                <button
                                    onClick={() => setShowManualInput(false)}
                                    className="w-full text-center text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white transition-colors"
                                >
                                    Enter website URL instead
                                </button>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </div>
    )
}
